import type { CSSProperties } from "react";

interface TypographyTextProps extends React.HTMLAttributes<HTMLElement> {
  children?: React.ReactNode;
  size?: TypographySize;
  weight?: TypographyFontWeight;
  color?: string;
  css?: CSSProperties;
  as?: "p" | "span" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  fontStyle?: "normal" | "italic";
  marginTop?: string | number;
  marginBottom?: string | number;
  marginLeft?: string | number;
  marginRight?: string | number;
  truncate?: boolean; // truncate only works with the p tag
  width?: string;
}

export type TypographyFontWeight = "regular" | "medium" | "semibold" | "bold";
export type TypographySize = "xxs" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl" | "7xl";

export default function TypographyText({
  children,
  size = "md",
  weight = "regular",
  color,
  css,
  as: Tag = "p",
  truncate = true,
  ...props
}: TypographyTextProps) {
  const fontSize = {
    xxs: "10px",
    xs: "12px",
    sm: "14px",
    md: "16px",
    lg: "18px",
    xl: "20px",
    "2xl": "24px",
    "3xl": "30px",
    "4xl": "36px",
    "5xl": "48px",
    "6xl": "60px",
    "7xl": "72px",
  }[size];

  const lineHeight = {
    xxs: "16px",
    xs: "18px",
    sm: "20px",
    md: "24px",
    lg: "28px",
    xl: "30px",
    "2xl": "32px",
    "3xl": "38px",
    "4xl": "44px",
    "5xl": "60px",
    "6xl": "72px",
    "7xl": "90px",
  }[size];

  const letterSpacing = {
    xxs: "0px",
    xs: "0px",
    sm: "0px",
    md: "0px",
    lg: "0px",
    xl: "0px",
    "2xl": "0px",
    "3xl": "0px",
    "4xl": "0px",
    "5xl": "-2%",
    "6xl": "-2%",
    "7xl": "-2%",
  }[size];

  const fontWeight = {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  }[weight];

  const styles: CSSProperties = {
    fontFamily: "Open Sauce One, sans-serif",
    fontSize: fontSize,
    fontWeight: fontWeight,
    lineHeight: lineHeight,
    letterSpacing: letterSpacing,
    fontStyle: props.fontStyle,
    color: color,
    whiteSpace: truncate ? "nowrap" : "initial",
    overflow: truncate ? "hidden" : "initial",
    textOverflow: truncate ? "ellipsis" : "initial",
    marginTop: props.marginTop,
    marginBottom: props.marginBottom,
    marginLeft: props.marginLeft,
    marginRight: props.marginRight,
    width: props.width,
    cursor: props.onClick ? "pointer" : undefined,
    ...css,
  };

  const { marginTop, marginBottom, marginLeft, marginRight, width, ...propsWithoutStyleProperties } = props;

  return (
    <Tag style={styles} {...propsWithoutStyleProperties}>
      {children}
    </Tag>
  );
}
